import { PaymentAccountTypeSelect } from "@components/inputs/PaymentAccountTypeSelect";
import RHFController from "@components/inputs/RHFController";
import MemberSettingsRenderer from "@components/utils/MemberSettingsRenderer";
import useContactLinks from "@hooks/useContactLinks";
import { useMemberSettings } from "@hooks/useMemberSettings";
import {
  Checkbox,
  cn,
  FundingProfileType,
  MemberSettingFundingMode,
  MemberSettingNameMode,
  TextField,
} from "kz-ui-sdk";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { InnerFormProps } from "../../types";

const fundingMode = MemberSettingFundingMode.EWalletOnly;

const EWalletInnerForm = ({ errors, control, isLoading, displayLegalName, allowEditDefault, fieldHelperTextManager }: InnerFormProps) => {
  const { t } = useTranslation();
  const { phoneMinLength, phoneMaxLength, phoneStartWith } = useMemberSettings();
  const { openContact } = useContactLinks();

  const handleOpenContactLink = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      openContact();
    },
    [openContact],
  );

  const accountNoPlaceholder = useMemo(() => {
    // start with phoneStartWith, fill the rest with 01234567890123
    const fillLength = phoneMinLength - phoneStartWith.length;
    let fillStr = "";
    for (let i = 1; i <= fillLength; i++) {
      fillStr += String(i % 10);
    }
    return `${phoneStartWith}${fillStr}`;
  }, [phoneMinLength, phoneStartWith]);

  return (
    <>
      {/* LEGAL NAME */}
      {displayLegalName && (
        <div>
          {/* FULL NAME */}
          <MemberSettingsRenderer
            feature="nameMode"
            match={MemberSettingNameMode.FullInFirst}
          >
            <label className="flex flex-col">
              <span className={cn("mb-0.5 text-sm text-content-base", { "text-error-base": errors.accountName })}>
                {t("full name")}
              </span>
              <RHFController
                control={control}
                name="accountName"
                fieldHelperTextManager={fieldHelperTextManager}
              >
                <TextField
                  placeholder={"John Doe"}
                  disabled={isLoading}
                  aria-label="E-wallet full name input"
                />
              </RHFController>
            </label>
          </MemberSettingsRenderer>

          {/* FIRST NAME & LAST NAME */}
          <MemberSettingsRenderer
            feature="nameMode"
            match={MemberSettingNameMode.FirstLast}
          >
            <div className="grid grid-cols-2 gap-4">
              <label className="flex flex-col">
                <span className={cn("mb-0.5 text-sm text-content-base", { "text-error-base": errors.firstName })}>
                  {t("first name")}
                </span>
                <RHFController
                  control={control}
                  name="firstName"
                  fieldHelperTextManager={fieldHelperTextManager}
                >
                  <TextField
                    placeholder={"John"}
                    disabled={isLoading}
                    aria-label="E-wallet first name input"
                  />
                </RHFController>
              </label>

              <label className="flex flex-col">
                <span className={cn("mb-0.5 text-sm text-content-base", { "text-error-base": errors.lastName })}>
                  {t("last name")}
                </span>
                <RHFController
                  control={control}
                  name="lastName"
                  fieldHelperTextManager={fieldHelperTextManager}
                >
                  <TextField
                    placeholder={"Doe"}
                    disabled={isLoading}
                    aria-label="E-wallet last name input"
                  />
                </RHFController>
              </label>
            </div>
          </MemberSettingsRenderer>

          {/* LEGAL NAME WARNING */}
          {/* {displayDisplayNameWarning && (
            <p className="text-xs leading-4 text-warning-base">{t("legal name warning")}</p>
          )} */}
        </div>
      )}

      {/* E-WALLET SELECT */}
      <label className="flex flex-col">
        <span className={cn("mb-0.5 text-sm text-content-base", { "text-error-base": errors.accountType })}>
          {t("e-wallet type")}
        </span>
        <RHFController
          control={control}
          name="accountType"
        >
          <PaymentAccountTypeSelect
            disabled={isLoading}
            accountType={FundingProfileType.EWallet}
          />
        </RHFController>
      </label>

      {/* E-WALLET NUMBER */}
      <div>
        <label className="flex flex-col">
          <span className={cn("mb-0.5 text-sm text-content-base", { "text-error-base": fieldHelperTextManager?.shouldShowError("accountNo")})}>
          {t("e-wallet number")}
          </span>
          <RHFController
            control={control}
            name="accountNo"
            hideErrorText={errors.accountNo?.message === t(`${fundingMode} is linked to another user.`)}
            helperTextParams={{
              minLength: phoneMinLength,
              maxLength: phoneMaxLength,
              startWith: Array.isArray(phoneStartWith) ? phoneStartWith.join(", ") : phoneStartWith,
              fundingMode: fundingMode || "",
            }}
            fieldHelperTextManager={fieldHelperTextManager}
          >
            <TextField
              placeholder={accountNoPlaceholder}
              disabled={isLoading}
            />
          </RHFController>
        </label>
        {errors.accountNo?.message === t(`${fundingMode} is linked to another user.`) && (
          <p className="text-right text-xs leading-4 text-error-base transition-[max-height]">
            {t(`${fundingMode} is linked to another user.`)}{" "}
            <button
              onClick={handleOpenContactLink}
              className="cursor-pointer underline"
            >
              {t("contact cs")}
            </button>
          </p>
        )}
      </div>

      {/* DEFAULT CHECKBOX */}
      <div>
        <label className="inline-flex items-center justify-start gap-[2px]">
          <RHFController
            control={control}
            name="isDefault"
          >
            <Checkbox
              variant="outlined"
              classes={{
                input_outlined: "checked:border-accentBrand-500 checked:bg-accentBrand-500 border-stroke-base border-2",
                "root&": !allowEditDefault ? "opacity-30" : "",
              }}
              disabled={!allowEditDefault}
            />
          </RHFController>

          <p className="text-sm text-content-base">{t("set as default")}</p>
        </label>
      </div>
    </>
  );
};

export default EWalletInnerForm;
